(() => {
  if (window.__popupPageInit) return;
  window.__popupPageInit = true;

  if (!customElements.get('event-day-carousel')) {
    customElements.define(
      'event-day-carousel',
      class EventDayCarousel extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-edc-track]');
          this.slides = Array.from(this.querySelectorAll('[data-edc-slide]'));
          this.empty = this.querySelector('[data-edc-empty]');
          this.buttons = Array.from(this.querySelectorAll('[data-day-nav]'));
          this.switching = false;

          this.buttons.forEach((button) => {
            button.addEventListener('click', () => this.setDay(button.dataset.dayNav));
          });

          const active = this.buttons.find((button) => button.classList.contains('is-active'));
          this.setDay(active ? active.dataset.dayNav : 'all', true);
        }

        setDay(day, instant = false) {
          const nextButton = this.buttons.find((button) => button.dataset.dayNav === day);
          if (!nextButton || this.switching) return;

          this.buttons.forEach((button) => {
            const on = button === nextButton;
            button.classList.toggle('is-active', on);
            button.setAttribute('aria-pressed', on ? 'true' : 'false');
          });

          const applyFilter = () => {
            let visible = 0;
            this.slides.forEach((slide) => {
              const slideDay = slide.dataset.day;
              const match = day === 'all' || slideDay === day || slideDay === 'all';
              slide.classList.toggle('is-hidden', !match);
              if (match) visible += 1;
            });

            this.empty?.classList.toggle('is-visible', visible === 0);
            if (this.track) this.track.scrollLeft = 0;
          };

          if (instant || !this.track) {
            applyFilter();
            return;
          }

          this.switching = true;
          this.track.classList.add('is-switching');

          window.setTimeout(() => {
            applyFilter();
            requestAnimationFrame(() => {
              this.track.classList.remove('is-switching');
              this.switching = false;
            });
          }, 220);
        }
      }
    );
  }

  const initAccordion = (root = document) => {
    root.querySelectorAll('[data-popup-days]').forEach((section) => {
      if (section.dataset.popupReady === 'true') return;
      section.dataset.popupReady = 'true';

      const days = Array.from(section.querySelectorAll('.popup-day'));
      const oneAtATime = section.dataset.oneAtATime !== 'false';
      const duration = 550;
      const easing = 'cubic-bezier(0.16, 1, 0.3, 1)';

      days.forEach((detailsEl) => {
        const summary = detailsEl.querySelector('summary');
        const body = detailsEl.querySelector('.popup-day__body');
        const inner = detailsEl.querySelector('.popup-day__inner');
        if (!summary || !body || !inner) return;

        let animating = false;
        let currentAnimation = null;

        const stopAnimation = () => {
          if (currentAnimation) {
            currentAnimation.cancel();
            currentAnimation = null;
          }
        };

        const contentHeight = () => inner.scrollHeight;

        const animateTo = (from, to, onDone) => {
          stopAnimation();
          body.style.height = `${from}px`;
          body.style.overflow = 'hidden';

          currentAnimation = body.animate([{ height: `${from}px` }, { height: `${to}px` }], {
            duration,
            easing,
            fill: 'forwards',
          });

          currentAnimation.onfinish = () => {
            stopAnimation();
            body.style.height = to === 0 ? '0px' : 'auto';
            if (onDone) onDone();
            animating = false;
          };
        };

        const openDay = () => {
          if (animating || detailsEl.classList.contains('is-open')) return;
          animating = true;
          detailsEl.open = true;
          body.style.height = '0px';
          void body.offsetHeight;
          detailsEl.classList.add('is-open');
          animateTo(0, contentHeight());
        };

        const closeDay = () => {
          if (animating || !detailsEl.classList.contains('is-open')) return;
          animating = true;
          const start = body.scrollHeight || contentHeight();
          detailsEl.classList.remove('is-open');
          animateTo(start, 0, () => {
            detailsEl.open = false;
          });
        };

        if (detailsEl.hasAttribute('open')) {
          detailsEl.classList.add('is-open');
          body.style.height = 'auto';
        } else {
          body.style.height = '0px';
        }

        summary.addEventListener('click', (event) => {
          event.preventDefault();
          const willOpen = !detailsEl.classList.contains('is-open');

          if (oneAtATime && willOpen) {
            days.forEach((other) => {
              if (other !== detailsEl) other.dispatchEvent(new CustomEvent('popup-close'));
            });
          }

          if (willOpen) openDay();
          else closeDay();
        });

        detailsEl.addEventListener('popup-close', closeDay);
      });
    });
  };

  const CALENDAR_FALLBACK_TZ = 'Europe/Copenhagen';

  const isValidTimeZone = (timeZone) => {
    if (!timeZone) return false;
    try {
      new Intl.DateTimeFormat('en-US', { timeZone });
      return true;
    } catch (error) {
      return false;
    }
  };

  const zoneOffsetMs = (date, timeZone) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(date)
      .reduce((acc, part) => {
        acc[part.type] = part.value;
        return acc;
      }, {});

    const hour = parts.hour === '24' ? 0 : Number(parts.hour);
    const asUTC = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      hour,
      Number(parts.minute),
      Number(parts.second)
    );

    return asUTC - date.getTime();
  };

  // Resolves a wall-clock time in timeZone to the matching UTC instant, so the
  // .ics can use absolute Z timestamps instead of shipping a VTIMEZONE block.
  const zonedTimeToUTC = (year, month, day, hour, minute, timeZone) => {
    const naive = Date.UTC(year, month - 1, day, hour, minute, 0);
    const firstPass = naive - zoneOffsetMs(new Date(naive), timeZone);
    // Re-resolve so days that cross a DST change land on the right offset.
    return new Date(naive - zoneOffsetMs(new Date(firstPass), timeZone));
  };

  const toICSStamp = (date) => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

  const escapeICS = (value) =>
    String(value == null ? '' : value)
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r?\n/g, '\\n');

  // RFC 5545 caps lines at 75 octets, and Danish characters are multi-byte.
  const foldICSLine = (line) => {
    const encoder = new TextEncoder();
    const chunks = [];
    let current = '';
    let bytes = 0;

    Array.from(line).forEach((char) => {
      const size = encoder.encode(char).length;
      const limit = chunks.length === 0 ? 75 : 74;

      if (bytes + size > limit) {
        chunks.push(current);
        current = '';
        bytes = 0;
      }

      current += char;
      bytes += size;
    });

    chunks.push(current);
    return chunks.join('\r\n ');
  };

  const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  const isRealDate = (year, month, day) => {
    if (month < 1 || month > 12 || day < 1) return false;

    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    const max = month === 2 && !leap ? 28 : DAYS_IN_MONTH[month - 1];

    return day <= max;
  };

  // Tåler både ÅÅÅÅ-MM-DD og dansk D-M-ÅÅÅÅ, med -, / eller . som skilletegn.
  const parseCalendarDate = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return null;

    let year;
    let month;
    let day;
    let match = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(raw);

    if (match) {
      year = Number(match[1]);
      month = Number(match[2]);
      day = Number(match[3]);
    } else {
      match = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(raw);
      if (!match) return null;

      day = Number(match[1]);
      month = Number(match[2]);
      year = Number(match[3]);
    }

    return isRealDate(year, month, day) ? { year, month, day } : null;
  };

  // Tåler 11, 11:00, 11.00 og 1100.
  const parseCalendarTime = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return null;

    let hour;
    let minute;
    const separated = /^(\d{1,2})(?:[:.](\d{2}))?$/.exec(raw);

    if (separated) {
      hour = Number(separated[1]);
      minute = separated[2] === undefined ? 0 : Number(separated[2]);
    } else {
      const compact = /^(\d{1,2})(\d{2})$/.exec(raw);
      if (!compact) return null;

      hour = Number(compact[1]);
      minute = Number(compact[2]);
    }

    return hour <= 23 && minute <= 59 ? { hour, minute } : null;
  };

  const dayDescription = (dayEl) => {
    const entries = [];

    dayEl.querySelectorAll('.popup-activity').forEach((activity) => {
      const title = activity.querySelector('.popup-activity__title')?.textContent.trim();
      const slot = activity.querySelector('.popup-activity__slot')?.textContent.trim();
      const text = activity.querySelector('.popup-activity__text')?.textContent.trim();
      const heading = [title, slot].filter(Boolean).join(' · ');
      const entry = [heading, text].filter(Boolean).join('\n');

      if (entry) entries.push(entry);
    });

    return entries.join('\n\n');
  };

  const buildEvent = (dayEl, config, stamp) => {
    const date = parseCalendarDate(dayEl.dataset.calDate);
    const start = parseCalendarTime(dayEl.dataset.calStart);
    const end = parseCalendarTime(dayEl.dataset.calEnd);
    if (!date || !start || !end) return null;

    const startAt = zonedTimeToUTC(date.year, date.month, date.day, start.hour, start.minute, config.timeZone);
    let endAt = zonedTimeToUTC(date.year, date.month, date.day, end.hour, end.minute, config.timeZone);

    if (endAt <= startAt) {
      endAt = zonedTimeToUTC(date.year, date.month, date.day + 1, end.hour, end.minute, config.timeZone);
    }

    const label = (dayEl.dataset.calLabel || '').trim();
    const summary = [config.title, label].filter(Boolean).join(' — ');
    const uid = `${dayEl.dataset.calUid || dayEl.dataset.calDate}@${window.location.hostname || 'popup'}`;
    const description = dayDescription(dayEl);

    const lines = [
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${stamp}`,
      `DTSTART:${toICSStamp(startAt)}`,
      `DTEND:${toICSStamp(endAt)}`,
      `SUMMARY:${escapeICS(summary)}`,
    ];

    if (config.location) lines.push(`LOCATION:${escapeICS(config.location)}`);
    if (config.url) lines.push(`URL:${escapeICS(config.url)}`);
    if (description) lines.push(`DESCRIPTION:${escapeICS(description)}`);

    lines.push('END:VEVENT');
    return lines;
  };

  const buildCalendar = (dayEls, config) => {
    const stamp = toICSStamp(new Date());
    const events = dayEls.map((dayEl) => buildEvent(dayEl, config, stamp)).filter(Boolean);
    if (!events.length) return null;

    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Duff Beauty//Pop-up//DA',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      ...events.flat(),
      'END:VCALENDAR',
    ];

    return `${lines.map(foldICSLine).join('\r\n')}\r\n`;
  };

  const slugify = (value, fallback) => {
    const slug = String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return slug || fallback;
  };

  const downloadICS = (content, filename) => {
    const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const initCalendar = (root = document) => {
    root.querySelectorAll('[data-popup-days]').forEach((section) => {
      if (section.dataset.calendarReady === 'true') return;

      const days = Array.from(section.querySelectorAll('.popup-day[data-cal-date]'));
      if (!days.length) return;

      section.dataset.calendarReady = 'true';

      const allButton = section.querySelector('[data-calendar-all]');
      const dayLabel = (dayEl) =>
        dayEl.querySelector('.popup-day__date')?.textContent.trim() || dayEl.dataset.calDate || 'dag';

      // Liquid kan kun se om felterne er udfyldt, ikke om de kan læses. Dage der
      // ikke kan fortolkes fjernes her, så de ikke efterlader en død knap.
      const usable = days.filter((dayEl) => {
        const ok =
          parseCalendarDate(dayEl.dataset.calDate) &&
          parseCalendarTime(dayEl.dataset.calStart) &&
          parseCalendarTime(dayEl.dataset.calEnd);

        if (!ok) {
          dayEl.querySelector('.popup-day__calendar')?.remove();
          console.warn(
            `[pop-up dagsprogram] Kan ikke læse dato/tid for "${dayLabel(dayEl)}" ` +
              `(dato: "${dayEl.dataset.calDate}", start: "${dayEl.dataset.calStart}", ` +
              `slut: "${dayEl.dataset.calEnd}"). Brug fx 2026-09-03 og 11:00.`
          );
        }

        return ok;
      });

      if (!usable.length) {
        allButton?.closest('.popup-days__calendar')?.remove();
        return;
      }

      const total = section.querySelectorAll('.popup-day').length;
      if (usable.length < total) {
        console.warn(
          `[pop-up dagsprogram] Kun ${usable.length} af ${total} dage har brugbar dato og tid. ` +
            'Resten kommer ikke med i "hele programmet".'
        );
      }

      const config = {
        title: (section.dataset.calTitle || '').trim() || 'Pop-up',
        location: (section.dataset.calLocation || '').trim(),
        url: (section.dataset.calUrl || '').trim(),
        timeZone: isValidTimeZone(section.dataset.calTimezone)
          ? section.dataset.calTimezone
          : CALENDAR_FALLBACK_TZ,
      };

      const save = (dayEls, filename) => {
        const content = buildCalendar(dayEls, config);
        if (content) downloadICS(content, filename);
      };

      const base = slugify(config.title, 'popup');

      allButton?.addEventListener('click', () => {
        save(usable, `${base}-program.ics`);
      });

      usable.forEach((dayEl) => {
        const date = parseCalendarDate(dayEl.dataset.calDate);
        const pad = (n) => String(n).padStart(2, '0');
        const stamp = `${date.year}-${pad(date.month)}-${pad(date.day)}`;

        dayEl.querySelector('[data-calendar-day]')?.addEventListener('click', () => {
          save([dayEl], `${base}-${stamp}.ics`);
        });
      });
    });
  };

  // Egen reveal, så sektionen ikke afhænger af about-page.js.
  const initReveal = (root = document) => {
    const targets = Array.from(root.querySelectorAll('[data-popup-reveal]')).filter(
      (el) => el.dataset.popupRevealReady !== 'true'
    );
    if (!targets.length) return;

    targets.forEach((el) => {
      el.dataset.popupRevealReady = 'true';
    });

    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-inview'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-inview');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );

    targets.forEach((el) => observer.observe(el));
  };

  const init = (root = document) => {
    initAccordion(root);
    initCalendar(root);
    initReveal(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
